import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Fair, Slot } from '../types';
import { formatCurrency, multiplyCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { formatDateRange, parseDateOnly } from '../utils/fairStatus'; // Issue #1672

const MAX_DATES = 365; // Backend limit per CLAUDE.md

interface DateSelectionCalendarProps {
  slot: Slot;
  fair: Fair;
  selectedDates: string[];
  unavailableDates: string[];
  onDateToggle: (date: string) => void;
  disabled?: boolean;
}

export default function DateSelectionCalendar({
  slot,
  fair,
  selectedDates,
  unavailableDates,
  onDateToggle,
  disabled = false,
}: DateSelectionCalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState(() => {
    return parseDateOnly(fair.start_date) ?? new Date();
  });

  // Generate fair date range (Issue #1672: parse in local timezone)
  const fairDates = useMemo(() => {
    const start = parseDateOnly(fair.start_date);
    const end = parseDateOnly(fair.end_date);
    if (!start || !end) return new Set<string>();

    const dates = new Set<string>();
    const current = new Date(start);
    while (current <= end) {
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      dates.add(`${year}-${month}-${day}`);
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }, [fair.start_date, fair.end_date]);

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];

    // Add days from previous month to fill first week
    const startPadding = firstDay.getDay();
    for (let i = startPadding - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }

    // Add days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push({ date: new Date(year, month, day), isCurrentMonth: true });
    }

    // Add days from next month to fill last week
    const endPadding = 6 - lastDay.getDay();
    for (let i = 1; i <= endPadding; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  }, [currentMonth]);

  const formatDateKey = (date: Date) => {
    // Issue #1672: Use local date components, not UTC, so keys match fairDates Set
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateInFairRange = (date: Date) => {
    return fairDates.has(formatDateKey(date));
  };

  const isDateUnavailable = (date: Date) => {
    return unavailableDates.includes(formatDateKey(date));
  };

  const isDateSelected = (date: Date) => {
    return selectedDates.includes(formatDateKey(date));
  };

  const handleDateClick = (date: Date) => {
    if (disabled) return;

    const dateKey = formatDateKey(date);
    if (!isDateInFairRange(date) || isDateUnavailable(date)) return;

    // Prevent selecting more than MAX_DATES (unless deselecting)
    const isCurrentlySelected = selectedDates.includes(dateKey);
    if (!isCurrentlySelected && selectedDates.length >= MAX_DATES) {
      return; // Already at max, can't add more
    }

    onDateToggle(dateKey);
  };

  const isAtMaxSelection = selectedDates.length >= MAX_DATES;

  const navigateMonth = (direction: number) => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + direction, 1));
  };

  const totalPrice = multiplyCurrency(slot.base_price, selectedDates.length); // Issue #905: Decimal.js calculation

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Select Dates</h3>
        <div className="text-right">
          <span className={`text-sm ${isAtMaxSelection ? 'text-amber-600 font-medium' : 'text-gray-600'}`}>
            {selectedDates.length} {selectedDates.length === 1 ? 'day' : 'days'} selected
            {isAtMaxSelection && ` (max ${MAX_DATES})`}
          </span>
          <div className="text-lg font-bold text-secondary">
            {formatCurrency(totalPrice)} {/* Issue #905: Decimal.js formatting */}
          </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
          disabled={disabled}
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h4 className="text-lg font-medium text-gray-900">
          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h4>
        <button
          onClick={() => navigateMonth(1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
          disabled={disabled}
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map(({ date, isCurrentMonth }, index) => {
          const inFairRange = isDateInFairRange(date);
          const unavailable = isDateUnavailable(date);
          const selected = isDateSelected(date);
          // Can click if: in range, not unavailable, not disabled, AND (selected OR not at max)
          const isClickable = inFairRange && !unavailable && !disabled && (selected || !isAtMaxSelection);

          return (
            <button
              key={index}
              onClick={() => handleDateClick(date)}
              disabled={!isClickable && !selected}
              aria-label={`${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}${selected ? ' (selected)' : ''}${unavailable ? ' (unavailable)' : ''}${!inFairRange ? ' (not in fair dates)' : ''}${isAtMaxSelection && !selected ? ' (max dates reached)' : ''}`}
              className={`
                relative aspect-square flex items-center justify-center text-sm rounded-lg transition-all
                ${!isCurrentMonth ? 'text-gray-300' : ''}
                ${!inFairRange ? 'text-gray-300 cursor-default' : ''}
                ${unavailable ? 'bg-red-50 text-red-300 line-through cursor-not-allowed' : ''}
                ${selected ? 'bg-teal-500 text-white font-semibold' : ''}
                ${isClickable && !selected ? 'hover:bg-secondary-light/100 cursor-pointer' : ''}
                ${inFairRange && !unavailable && !selected ? 'bg-teal-50 text-primary-dark font-medium' : ''}
                ${isAtMaxSelection && !selected && inFairRange && !unavailable ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              {date.getDate()}
              {selected && (
                <div className="absolute bottom-0.5 right-0.5">
                  <Check className="w-3 h-3" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-teal-50 rounded border border-teal-200"></div>
          <span className="text-sm text-gray-600">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-teal-500 rounded"></div>
          <span className="text-sm text-gray-600">Selected</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-50 rounded border border-red-200"></div>
          <span className="text-sm text-gray-600">Unavailable</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-100 rounded"></div>
          <span className="text-sm text-gray-600">Not in fair dates</span>
        </div>
      </div>

      {/* Date Range Info */}
      {fair.start_date && fair.end_date && (
        <p className="text-sm text-gray-500 text-center">
          Fair dates: {formatDateRange(fair.start_date, fair.end_date)}
        </p>
      )}
    </div>
  );
}
